import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Table, Button, Input, Space, Typography, Card, Divider, Row, Col, Tag, Modal, Spin, Alert, Tabs, Progress, Collapse, Badge } from "antd";
import { DeleteOutlined, ExclamationCircleOutlined, ReloadOutlined, CloudUploadOutlined, CheckCircleFilled, ExclamationCircleFilled, CloseCircleFilled, FileExcelOutlined, DownloadOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "../Footer/Footer";
import GLOBALS from "../../utils/Globals";
import axios from "axios";
import { getUserProfileAction } from "../../redux/slices/users/usersSlice";
import ProductThumbnail from "../Common/ProductThumbnail";
// import io from 'socket.io-client';  // Temporarily disabled

const { Title, Text } = Typography;

// Helper function to format field names
const formatFieldName = (fieldName) => {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to clean UPC/GTIN values by removing spaces
const cleanUPCValue = (value) => {
  if (!value || typeof value !== 'string') return value;
  return value.replace(/\s+/g, ''); // Remove all whitespace characters
};

// Function to validate GTIN using the GS1 service
const validateGTIN = async (gtin, type = 'case') => {
  if (!gtin || !gtin.trim()) {
    return {
      status: "not_validated",
      message: "No GTIN provided",
      timestamp: new Date().toISOString()
    };
  }

  try {
    const baseURLFetchGS1GTIN =
      process.env.REACT_APP_ENV === "PROD"
        ? GLOBALS.PROD.baseURLFetchGS1GTIN
        : GLOBALS.DEV.baseURLFetchGS1GTIN;

    const formDataGTIN = new FormData();
    formDataGTIN.append('gtin', gtin.trim());

    const gtinResponse = await axios.post(baseURLFetchGS1GTIN, formDataGTIN, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    console.log(`🌐🌐🌐 === GTIN SERVICE RESPONSE (${type}) ===`);
    console.log('🔗 URL:', baseURLFetchGS1GTIN);
    console.log('📤 Sent GTIN:', gtin);
    console.log('📥 Full Response:', gtinResponse);
    console.log('📊 Response Status:', gtinResponse.status);
    console.log('📋 Response Headers:', gtinResponse.headers);
    console.log('📦 Response Data:', gtinResponse.data);
    console.log('📦 Response Data Type:', typeof gtinResponse.data);
    console.log('📦 Response Data Length:', Array.isArray(gtinResponse.data) ? gtinResponse.data.length : 'Not an array');
    if (Array.isArray(gtinResponse.data) && gtinResponse.data.length > 0) {
      console.log('📦 First Item:', gtinResponse.data[0]);
      console.log('📦 First Item Keys:', Object.keys(gtinResponse.data[0]));
    }
    console.log('🌐🌐🌐 ========================================');

    const responseData = gtinResponse.data;
    let hasValidationErrors = false;
    let errorMessage = '';

    console.log('🔍🔍🔍 === ANALYZING GTIN RESPONSE ===');
    console.log('📊 Response is array?', Array.isArray(responseData));
    console.log('📊 Response has products?', !!responseData?.products);
    console.log('📊 Products is array?', Array.isArray(responseData?.products));
    console.log('📊 Products length:', responseData?.products?.length);

    // Handle the response format: {products: [...]}
    let productsArray = null;
    if (responseData?.products && Array.isArray(responseData.products)) {
      productsArray = responseData.products;
    } else if (Array.isArray(responseData)) {
      // Fallback for direct array format
      productsArray = responseData;
    }

    console.log('📦 Products array:', productsArray);

    // Check for validation errors in the response
    if (productsArray && productsArray.length > 0) {
      const firstResult = productsArray[0];
      console.log('🔍 First result:', firstResult);
      console.log('🔍 Has validationErrors?', !!firstResult.validationErrors);
      console.log('🔍 ValidationErrors:', firstResult.validationErrors);
      console.log('🔍 Has gtinRecordStatus?', !!firstResult.gtinRecordStatus);
      console.log('🔍 gtinRecordStatus:', firstResult.gtinRecordStatus);

      if (firstResult.validationErrors && firstResult.validationErrors.length > 0) {
        hasValidationErrors = true;
        errorMessage = firstResult.validationErrors.map(err =>
          err.errors ? err.errors.map(e => e.message).join(', ') : err
        ).join(', ');
        console.log('❌ Validation errors found:', errorMessage);
      }
    }

    console.log('🔍 Final analysis:', { hasValidationErrors, errorMessage, hasProductsArray: !!productsArray });
    console.log('🔍🔍🔍 === END ANALYSIS ===');

    if (hasValidationErrors) {
      console.log('❌ Returning INVALID status');
      return {
        status: "invalid",
        message: errorMessage,
        timestamp: new Date().toISOString(),
        response: responseData
      };
    } else if (productsArray && productsArray.length > 0) {
      console.log('✅ Returning VALID status');
      return {
        status: "valid",
        message: "GTIN validation successful",
        timestamp: new Date().toISOString(),
        response: responseData
      };
    } else {
      console.log('❌ Returning INVALID status - no data');
      return {
        status: "invalid",
        message: "No data found for this GTIN",
        timestamp: new Date().toISOString(),
        response: responseData
      };
    }
  } catch (error) {
    console.error('GTIN validation error:', error);
    return {
      status: "error",
      message: `Validation error: ${error.message}`,
      timestamp: new Date().toISOString()
    };
  }
};

// Function to calculate GTIN validation status for a record
const getGTINValidationStatus = (record) => {
  let validCount = 0;
  let totalCount = 0;

  // Check case validation
  if (record.case?.validation?.gtin_valid !== undefined) {
    totalCount++;
    if (record.case.validation.gtin_valid === true) {
      validCount++;
    }
  }

  // Check products validation
  if (record.products && Array.isArray(record.products)) {
    record.products.forEach(product => {
      if (product.validation?.gtin_valid !== undefined) {
        totalCount++;
        if (product.validation.gtin_valid === true) {
          validCount++;
        }
      }
    });
  }

  // Return status based on validation results
  if (totalCount === 0) {
    return 'none'; // No validation data
  } else if (validCount === totalCount) {
    return 'all'; // All valid
  } else if (validCount > 0) {
    return 'some'; // Some valid
  } else {
    return 'none_valid'; // None valid
  }
};

// Function to render GTIN validation icon
const renderGTINValidationIcon = (record) => {
  const status = getGTINValidationStatus(record);

  switch (status) {
    case 'all':
      return <CheckCircleFilled style={{ color: '#52c41a', fontSize: '16px' }} title="All GTINs Valid" />;
    case 'some':
      return <ExclamationCircleFilled style={{ color: '#faad14', fontSize: '16px' }} title="Some GTINs Valid" />;
    case 'none_valid':
      return <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: '16px' }} title="No GTINs Valid" />;
    case 'none':
    default:
      return <ExclamationCircleFilled style={{ color: '#d9d9d9', fontSize: '16px' }} title="No Validation Data" />;
  }
};

// Function to get validation status for a specific field
const getFieldValidationStatus = (record, fieldPath) => {
  const fieldName = fieldPath.split('.').pop();

  // Debug logging for validation lookup
  if (fieldName === 'Case UPC' || fieldName === 'Retail Pkg UPC' || fieldName === 'Retail Pkg. UPC') {
    console.log(`Checking validation for ${fieldName} at path: ${fieldPath}`);
  }

  // Check for Case UPC validation
  if (fieldName === 'Case UPC') {
    // Check the actual structure similar to products: validation.gtin_valid
    if (record.validation?.gtin_valid !== undefined) {
      return record.validation.gtin_valid;
    }
    if (record.case?.validation?.gtin_valid !== undefined) {
      return record.case.validation.gtin_valid;
    }
    // Try other validation structures
    if (record.validation?.case_upc_validation !== undefined) {
      return record.validation.case_upc_validation;
    }
    if (record.case?.validation?.case_upc_validation !== undefined) {
      return record.case.validation.case_upc_validation;
    }
    // Check legacy structure
    if (record.validation?.gtin_validation !== undefined) {
      return record.validation.gtin_validation;
    }
  }

  // Check for Retail Pkg UPC validation in products
  if (fieldName === 'Retail Pkg. UPC' || fieldName === 'Retail Pkg UPC') {
    // Extract product index from fieldPath (e.g., "products.0.product_information.Retail Pkg UPC")
    const pathParts = fieldPath.split('.');

    if (pathParts[0] === 'products' && pathParts[1]) {
      const productIndex = parseInt(pathParts[1]);

      if (record.products && record.products[productIndex]) {
        const product = record.products[productIndex];

        // Check the actual structure from your data: validation.gtin_valid
        if (product.validation?.gtin_valid !== undefined) {
          return product.validation.gtin_valid;
        }
        // Try other validation structures
        if (product.validation?.retail_pkg_upc_validation !== undefined) {
          return product.validation.retail_pkg_upc_validation;
        }
        // Check legacy structure
        if (product.validation?.gtin_validation !== undefined) {
          return product.validation.gtin_validation;
        }
      }
    }
  }

  return undefined;
};

// Function to render validation badge
const renderValidationBadge = (validationStatus) => {
  if (validationStatus === true || validationStatus === 'valid') {
    return <Tag color="green" size="small">Valid</Tag>;
  } else if (validationStatus === false || validationStatus === 'invalid') {
    return <Tag color="red" size="small">Invalid</Tag>;
  } else if (validationStatus === 'not_validated') {
    return <Tag color="orange" size="small">Not Validated</Tag>;
  }

  return null;
};

// Editable Field Component
const EditableField = ({ value, fieldPath, record, onUpdate }) => {
  const [editValue, setEditValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Check if this is an array field
  const isArrayField = Array.isArray(value);

  const fieldName = fieldPath.split('.').pop();
  const isGTINField = fieldName === 'Case UPC' || fieldName === 'Retail Pkg. UPC' || fieldName === 'Retail Pkg UPC';
  const validationStatus = getFieldValidationStatus(record, fieldPath);

  // Debug UPC field detection
  if (fieldName.includes('UPC')) {
    console.log('🎯 UPC FIELD DETECTED:', {
      fieldName,
      fieldPath,
      isGTINField,
      exactMatches: {
        'Case UPC': fieldName === 'Case UPC',
        'Retail Pkg. UPC': fieldName === 'Retail Pkg. UPC',
        'Retail Pkg UPC': fieldName === 'Retail Pkg UPC'
      }
    });
  }

  // Only log UPC fields and a few others to reduce noise
  if (isGTINField || fieldName.includes('UPC') || Math.random() < 0.1) {
    console.log('🏷️ EditableCell setup:', {
      fieldName,
      fieldPath,
      isGTINField,
      validationStatus,
      value,
      recordId: record?.metadata?.job_id || 'No job_id'
    });
  }



  const handleSave = async () => {
    console.log('🔧🔧🔧 === HANDLE SAVE START ===');
    console.log('🔧 handleSave called:', {
      editValue,
      value,
      fieldName,
      fieldPath,
      isGTINField,
      record: record?.metadata?.job_id || 'No job_id'
    });

    // Clean the UPC value by removing spaces for GTIN fields, or handle arrays
    let cleanedValue;
    if (isGTINField) {
      cleanedValue = cleanUPCValue(editValue);
    } else if (isArrayField) {
      // For arrays, ensure we have a proper array
      cleanedValue = Array.isArray(editValue) ? editValue : [editValue].filter(item => item);
    } else {
      cleanedValue = editValue;
    }
    console.log('🧹 Cleaned value:', { original: editValue, cleaned: cleanedValue, isArrayField });

    // Check if values are different (handle arrays specially)
    let valuesAreDifferent;
    if (isArrayField && Array.isArray(value) && Array.isArray(cleanedValue)) {
      valuesAreDifferent = JSON.stringify(value) !== JSON.stringify(cleanedValue);
    } else {
      valuesAreDifferent = editValue !== value;
    }
    console.log('📊 Value comparison:', {
      editValue,
      value,
      cleanedValue,
      valuesAreDifferent,
      editValueType: typeof editValue,
      valueType: typeof value,
      isArrayField
    });

    // Always perform GTIN validation for GTIN fields, even if value hasn't changed
    let validationResult = null;
    if (isGTINField && cleanedValue && cleanedValue.trim()) {
      console.log('🔍 Starting GTIN validation:', { fieldName, cleanedValue, validationType: fieldName === 'Case UPC' ? 'case' : 'product' });
      setIsValidating(true);
      try {
        const validationType = fieldName === 'Case UPC' ? 'case' : 'product';
        console.log('📞 Calling validateGTIN function...');
        console.log('📞 validateGTIN function exists:', typeof validateGTIN);

        validationResult = await validateGTIN(cleanedValue, validationType);

        console.log(`=== GTIN VALIDATION RESULT (${fieldName}) ===`);
        console.log('Field:', fieldName);
        console.log('Value:', cleanedValue);
        console.log('Result:', validationResult);
        console.log('Result type:', typeof validationResult);
        console.log('Result status:', validationResult?.status);
        console.log('==========================================');

        // Show validation result to user
        if (validationResult.status === 'valid') {
          toast.success(`${fieldName} validation successful!`, {
            position: "top-right",
            autoClose: 3000,
          });
        } else if (validationResult.status === 'invalid') {
          toast.warning(`${fieldName} validation failed: ${validationResult.message}`, {
            position: "top-right",
            autoClose: 5000,
          });
        } else if (validationResult.status === 'error') {
          toast.error(`${fieldName} validation error: ${validationResult.message}`, {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } catch (error) {
        console.error('❌ GTIN validation error:', error);
        console.error('❌ Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        toast.error(`Failed to validate ${fieldName}: ${error.message}`, {
          position: "top-right",
          autoClose: 5000,
        });
      } finally {
        setIsValidating(false);
      }
    } else if (isGTINField) {
      console.log('⚠️ Skipping GTIN validation:', {
        isGTINField,
        hasCleanedValue: !!cleanedValue,
        cleanedValueTrimmed: cleanedValue?.trim(),
        reason: !cleanedValue ? 'No cleaned value' : !cleanedValue.trim() ? 'Empty after trim' : 'Unknown'
      });
    }

    // Check if we should update the database
    const shouldUpdate = valuesAreDifferent || validationResult;
    console.log('🤔 Should update database?', {
      shouldUpdate,
      valueChanged: valuesAreDifferent,
      hasValidationResult: !!validationResult,
      validationResult,
      isArrayField
    });

    if (shouldUpdate) {
      console.log('💾💾💾 === DATABASE UPDATE START ===');

      // Simulate the params structure that onUpdate expects
      const params = {
        data: record,
        column: { colDef: { field: fieldPath } },
        newValue: cleanedValue,
        oldValue: value,
        node: { data: record },
        api: {
          refreshCells: () => {}, // Mock function
        }
      };

      console.log('📦 Base params created:', {
        fieldPath: params.column.colDef.field,
        newValue: params.newValue,
        oldValue: params.oldValue,
        recordId: params.data?.metadata?.job_id || 'No job_id'
      });

      // Add validation result to params for saving to MongoDB
      if (validationResult) {
        params.validationResult = {
          field: fieldName,
          gtin_valid: validationResult.status === 'valid',
          validation_details: validationResult
        };
        console.log('💾 Adding validation result to save params:', params.validationResult);
      }

      try {
        console.log('💾 Calling onUpdate with full params:', params);
        console.log('💾 onUpdate function type:', typeof onUpdate);

        await onUpdate(params);

        console.log('✅✅✅ onUpdate completed successfully');

        // Update the local value to reflect the change
        console.log('🔄 Updating local state from', value, 'to', cleanedValue);

        // Force a re-render to update validation badges
        if (validationResult && isGTINField) {
          console.log('🔄 Triggering data refresh to update validation badges');
          // The parent component should refresh the data
          window.dispatchEvent(new CustomEvent('refreshProductsData'));
        }

      } catch (error) {
        console.error('❌❌❌ Error in onUpdate:', error);
        console.error('❌ Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        toast.error(`Failed to save changes: ${error.message}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } else {
      console.log('⏭️ Skipping database update - no changes needed');
    }

    console.log('🏁 Setting isEditing to false');
    setIsEditing(false);
    console.log('🔧🔧🔧 === HANDLE SAVE END ===');
  };

  if (isEditing) {
    console.log('🎯 Rendering input field:', {
      editValue,
      isValidating,
      fieldName,
      fieldPath,
      isArrayField
    });

    // For array fields, provide a textarea for editing
    if (isArrayField) {
      const arrayAsText = Array.isArray(editValue) ? editValue.join('\n') : '';

      return (
        <div style={{ position: 'relative' }}>
          <Input.TextArea
            value={arrayAsText}
            onChange={(e) => {
              const lines = e.target.value.split('\n').map(line => line.trim()).filter(line => line.length > 0);
              console.log('✏️ Array input onChange:', {
                oldValue: editValue,
                newValue: lines,
                fieldName
              });
              setEditValue(lines);
            }}
            onBlur={() => {
              console.log('👁️ Array input blur, calling handleSave');
              handleSave();
            }}
            autoFocus
            size="small"
            disabled={isValidating}
            rows={Math.min(Math.max(Array.isArray(editValue) ? editValue.length : 1, 2), 6)}
            placeholder="Enter each item on a new line"
          />
        </div>
      );
    }

    // For regular fields, use the standard input
    return (
      <div style={{ position: 'relative' }}>
        <Input
          value={editValue}
          onChange={(e) => {
            console.log('✏️ Input onChange:', {
              oldValue: editValue,
              newValue: e.target.value,
              fieldName
            });
            setEditValue(e.target.value);
          }}
          onPressEnter={() => {
            console.log('⏎ Enter pressed, calling handleSave');
            handleSave();
          }}
          onBlur={() => {
            console.log('👁️ Input blur, calling handleSave');
            handleSave();
          }}
          autoFocus
          size="small"
          disabled={isValidating}
        />
        {isValidating && (
          <div style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '12px',
            color: '#999',
            pointerEvents: 'none'
          }}>
            Validating...
          </div>
        )}
      </div>
    );
  }

  // Helper function to safely render value
  const renderValue = (val) => {
    if (val === null || val === undefined) {
      return <Text type="secondary">Click to edit</Text>;
    }

    // If it's an array, display as a formatted list
    if (Array.isArray(val)) {
      if (val.length === 0) {
        return <Text type="secondary">Empty list</Text>;
      }
      if (val.length === 1) {
        return <span>{val[0]}</span>;
      }
      return (
        <div>
          <div>{val[0]}</div>
          <Text type="secondary" style={{ fontSize: '10px' }}>
            +{val.length - 1} more item{val.length > 2 ? 's' : ''}
          </Text>
        </div>
      );
    }

    // If it's an object, try to extract meaningful information
    if (typeof val === 'object') {
      // Handle error objects
      if (val.status && val.message) {
        return <Text type="danger">Error: {val.message}</Text>;
      }
      // Handle other objects by converting to JSON string
      try {
        return JSON.stringify(val);
      } catch (e) {
        return <Text type="secondary">Invalid data</Text>;
      }
    }

    // For primitive values, convert to string
    return String(val);
  };

  // Only log UPC fields to reduce noise
  if (isGTINField || fieldName.includes('UPC')) {
    console.log('🎨 Rendering display mode:', {
      value,
      isGTINField,
      validationStatus,
      fieldName,
      isEditing
    });
  }

  return (
    <div
      onClick={() => {
        console.log('👆 Clicked to edit field:', fieldName);
        setIsEditing(true);
      }}
      style={{
        cursor: 'pointer',
        minHeight: '22px',
        padding: '2px 4px',
        borderRadius: '2px',
        border: '1px solid transparent'
      }}
      onMouseEnter={(e) => e.target.style.border = '1px solid #d9d9d9'}
      onMouseLeave={(e) => e.target.style.border = '1px solid transparent'}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{renderValue(value)}</span>
        {isGTINField && (
          <div style={{ marginLeft: '8px' }}>
            {renderValidationBadge(validationStatus)}
          </div>
        )}
      </div>
    </div>
  );
};

// Detail Component for Ant Design Table
const DetailComponent = ({ record, onUpdate }) => {
  const caseData = useMemo(() => record?.case || {}, [record?.case]);
  const productsData = useMemo(() => record?.products || [], [record?.products]);

  // Debug: Log all available fields to help find UPC fields
  React.useEffect(() => {
    console.log('🗂️ === AVAILABLE FIELDS DEBUG ===');
    console.log('📁 Case data keys:', Object.keys(caseData));
    if (caseData.case_information) {
      console.log('📋 Case information fields:', Object.keys(caseData.case_information));
    }
    if (productsData.length > 0) {
      console.log('📦 Products count:', productsData.length);
      productsData.forEach((product, index) => {
        if (product.product_information) {
          console.log(`📦 Product ${index} fields:`, Object.keys(product.product_information));
        }
      });
    }
    console.log('🗂️ === END FIELDS DEBUG ===');
  }, [caseData, productsData]);

  // Case Information Sections
  const caseSections = [
    { key: "case_information", label: "Case Information" },
    { key: "microbiological_properties", label: "Microbiological Properties" },
    { key: "physical_chemical_attributes", label: "Physical & Chemical Attributes" },
    { key: "product_claims_certifications", label: "Product Claims & Certifications" }
  ];

  // Product Sections
  const productSections = [
    { key: "product_information", label: "Product Information" },
    { key: "ingredients", label: "Ingredients" },
    { key: "allergens", label: "Allergens" },
    { key: "nutritional_information", label: "Nutritional Information" },
    { key: "microbiological_properties", label: "Microbiological Properties" },
    { key: "physical_chemical_attributes", label: "Physical & Chemical Attributes" },
    { key: "product_claims_certifications", label: "Product Claims & Certifications" },
    { key: "validation", label: "Validation" }
  ];

  return (
    <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
      {/* Case Information Section */}
      <Card 
        title={<Title level={4} style={{ color: '#2563eb', margin: 0 }}>Case Information</Title>}
        style={{ marginBottom: '24px' }}
        size="small"
      >
        {caseSections.map((section) => {
          const sectionData = caseData[section.key];
          
          if (!sectionData || Object.keys(sectionData).length === 0) return null;

          return (
            <div key={`case-${section.key}`} style={{ marginBottom: '16px' }}>
              <Title level={5} style={{ color: '#374151', marginBottom: '8px' }}>
                {section.label}
              </Title>
              <Row gutter={[16, 8]}>
                {Object.entries(sectionData).filter(([fieldKey, fieldValue]) => {
                  // Filter out invalid field values that might cause rendering issues
                  return fieldKey && fieldValue !== undefined;
                }).map(([fieldKey, fieldValue]) => (
                  <Col span={12} key={fieldKey}>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong style={{ fontSize: '12px', color: '#6b7280' }}>
                        {formatFieldName(fieldKey)}:
                      </Text>
                      <div style={{ marginTop: '2px' }}>
                        <EditableField
                          value={fieldValue}
                          fieldPath={`case.${section.key}.${fieldKey}`}
                          record={record}
                          onUpdate={onUpdate}
                        />
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
              {section.key !== caseSections[caseSections.length - 1].key && <Divider />}
            </div>
          );
        })}
      </Card>

      {/* Products Section */}
      <Card
        title={<Title level={4} style={{ color: '#059669', margin: 0 }}>Products ({productsData.length})</Title>}
        size="small"
      >
        {productsData.length > 0 ? (
          <Tabs
            type="card"
            size="small"
            style={{ marginTop: '8px' }}
            items={productsData.map((product, productIndex) => ({
              key: `product-${productIndex}`,
              label: `${product.product_information?.name || product.name || `Product ${productIndex + 1}`}`,
              children: (
                <div>
                  {productSections.map((section) => {
                    const sectionData = product[section.key];

                    if (!sectionData || Object.keys(sectionData).length === 0) return null;

                    return (
                      <div key={`product-${productIndex}-${section.key}`} style={{ marginBottom: '16px' }}>
                        <Text strong style={{ fontSize: '13px', color: '#374151' }}>
                          {section.label}
                        </Text>
                        <Row gutter={[16, 8]} style={{ marginTop: '8px' }}>
                          {Object.entries(sectionData).filter(([fieldKey, fieldValue]) => {
                            // Filter out invalid field values that might cause rendering issues
                            return fieldKey && fieldValue !== undefined;
                          }).map(([fieldKey, fieldValue]) => (
                            <Col span={12} key={fieldKey}>
                              <div style={{ marginBottom: '6px' }}>
                                <Text style={{ fontSize: '11px', color: '#6b7280' }}>
                                  {formatFieldName(fieldKey)}:
                                </Text>
                                <div style={{ marginTop: '2px' }}>
                                  <EditableField
                                    value={fieldValue}
                                    fieldPath={`products.${productIndex}.${section.key}.${fieldKey}`}
                                    record={record}
                                    onUpdate={onUpdate}
                                  />
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                        {section.key !== productSections[productSections.length - 1].key && <Divider style={{ margin: '8px 0' }} />}
                      </div>
                    );
                  })}
                </div>
              )
            }))}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            No products available
          </div>
        )}
      </Card>
    </div>
  );
};

function Products() {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRowData, setDeleteRowData] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishModalVisible, setPublishModalVisible] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);
  const [publishStatus, setPublishStatus] = useState('');
  // const [websocket, setWebsocket] = useState(null);  // Temporarily disabled
  const [sessionId, setSessionId] = useState(null);
  const [resultsModalVisible, setResultsModalVisible] = useState(false);
  const [publishResults, setPublishResults] = useState(null);
  const [publishStartTime, setPublishStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Duplicate detection modal state
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [newDataForReplacement, setNewDataForReplacement] = useState(null);

  // Published status state
  const [publishedJobIds, setPublishedJobIds] = useState({});
  const [failedJobIds, setFailedJobIds] = useState({});
  const [publishHistory, setPublishHistory] = useState([]);

  const dispatch = useDispatch();

  // Function to show duplicate modal
  const showDuplicateModal = (duplicateData, newData) => {
    setDuplicateInfo(duplicateData);
    setNewDataForReplacement(newData);
    setDuplicateModalVisible(true);
  };

  // Function to handle duplicate replacement
  const handleReplaceDuplicate = async () => {
    try {
      const baseURLReplaceDuplicate = process.env.REACT_APP_ENV === "PROD"
        ? GLOBALS.PROD.baseURLReplaceDuplicate
        : GLOBALS.DEV.baseURLReplaceDuplicate;

      const response = await axios.post(baseURLReplaceDuplicate, {
        existing_document_id: duplicateInfo.existing_document_id,
        new_data: newDataForReplacement
      });

      if (response.status === 200) {
        toast.success(`Document "${duplicateInfo.haribo_item_no}" has been successfully overwritten with new data!`, {
          position: "bottom-right",
          autoClose: 3000,
        });

        // Refresh the data
        window.location.reload(); // Simple refresh for now

        // Close modal
        setDuplicateModalVisible(false);
        setDuplicateInfo(null);
        setNewDataForReplacement(null);
      }
    } catch (error) {
      console.error('Error replacing duplicate:', error);
      toast.error('Failed to replace document. Please try again.', {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  };

  // Monitor publish progress changes for debugging
  useEffect(() => {
    if (publishProgress > 0) {
      console.log(`📊 Progress state changed to: ${publishProgress}%`);
    }
  }, [publishProgress]);

  // Timer effect for publishing elapsed time
  useEffect(() => {
    let interval = null;
    if (publishModalVisible && publishStartTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - publishStartTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    } else {
      setElapsedTime(0);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [publishModalVisible, publishStartTime]);

  // Helper function to format elapsed time
  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Monitor rowData changes for debugging
  useEffect(() => {
    console.log('=== RowData Changed ===');
    console.log('Length:', rowData?.length || 0);
    console.log('Sample data:', rowData?.slice(0, 2));
    console.log('=====================');
  }, [rowData]);

  useEffect(() => {
    const handleFetchData = async () => {
      try {
        const result = await dispatch(getUserProfileAction());
        console.log("Data fetched successfully:", result.payload.user.email);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    handleFetchData();
  }, [dispatch]);

  // Extract S3 key from full URL
  const extractS3Key = (url) => {
    if (!url) return null;

    // If it's already just a key (no https://), return as is
    if (!url.startsWith('https://')) {
      return url;
    }

    // Extract key from full S3 URL
    // Format: https://bucket.s3.region.amazonaws.com/key
    try {
      const urlObj = new URL(url);
      return urlObj.pathname.substring(1); // Remove leading slash
    } catch (e) {
      console.warn('Failed to parse S3 URL:', url);
      return null;
    }
  };

  // Generate signed URLs for thumbnails
  const generateSignedUrls = async (records) => {
    try {
      // Collect all S3 keys from all products
      const s3Keys = [];
      const keyMap = new Map(); // Map to track which keys belong to which products

      records.forEach((record, recordIndex) => {
        if (record.products) {
          record.products.forEach((product, productIndex) => {
            if (product.pdf_thumbnail) {
              const s3Key = extractS3Key(product.pdf_thumbnail);
              if (s3Key) {
                s3Keys.push(s3Key);
                keyMap.set(s3Keys.length - 1, { recordIndex, productIndex, type: 'pdf_thumbnail' });
              }
            }
            if (product.thumbnails && Array.isArray(product.thumbnails)) {
              product.thumbnails.forEach((thumbnail, thumbIndex) => {
                const s3Key = extractS3Key(thumbnail);
                if (s3Key) {
                  s3Keys.push(s3Key);
                  keyMap.set(s3Keys.length - 1, { recordIndex, productIndex, type: 'thumbnails', thumbIndex });
                }
              });
            }
            if (product.barcode_thumbnails && Array.isArray(product.barcode_thumbnails)) {
              product.barcode_thumbnails.forEach((thumbnail, thumbIndex) => {
                const s3Key = extractS3Key(thumbnail);
                if (s3Key) {
                  s3Keys.push(s3Key);
                  keyMap.set(s3Keys.length - 1, { recordIndex, productIndex, type: 'barcode_thumbnails', thumbIndex });
                }
              });
            }
          });
        }
      });

      if (s3Keys.length === 0) {
        console.log('🔗 No S3 keys found for signed URL generation');
        return records;
      }

      console.log(`🔗 Generating signed URLs for ${s3Keys.length} S3 keys`);

      // Call backend to generate signed URLs
      const baseURLGenerateSignedURLs = process.env.REACT_APP_ENV === "PROD"
        ? GLOBALS.PROD.baseURLGenerateSignedURLs
        : GLOBALS.DEV.baseURLGenerateSignedURLs;

      const response = await axios.post(baseURLGenerateSignedURLs, {
        s3_keys: s3Keys
      });

      if (response.data.success) {
        const signedUrls = response.data.signed_urls;
        console.log(`✅ Generated ${signedUrls.length} signed URLs`);

        // Update records with signed URLs
        const updatedRecords = [...records];
        signedUrls.forEach((signedUrl, index) => {
          const mapping = keyMap.get(index);
          if (mapping && signedUrl) {
            const { recordIndex, productIndex, type, thumbIndex } = mapping;

            if (type === 'pdf_thumbnail') {
              updatedRecords[recordIndex].products[productIndex].pdf_thumbnail_url = signedUrl;
            } else if (type === 'thumbnails') {
              if (!updatedRecords[recordIndex].products[productIndex].thumbnail_urls) {
                updatedRecords[recordIndex].products[productIndex].thumbnail_urls = [];
              }
              updatedRecords[recordIndex].products[productIndex].thumbnail_urls[thumbIndex] = signedUrl;
            } else if (type === 'barcode_thumbnails') {
              if (!updatedRecords[recordIndex].products[productIndex].barcode_thumbnail_urls) {
                updatedRecords[recordIndex].products[productIndex].barcode_thumbnail_urls = [];
              }
              updatedRecords[recordIndex].products[productIndex].barcode_thumbnail_urls[thumbIndex] = signedUrl;
            }
          }
        });

        return updatedRecords;
      } else {
        console.error('❌ Failed to generate signed URLs');
        return records;
      }
    } catch (error) {
      console.error('❌ Error generating signed URLs:', error);
      return records;
    }
  };

  // Fetch products data
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const baseURLGetAllPDFDocs =
          process.env.REACT_APP_ENV === "PROD"
            ? GLOBALS.PROD.baseURLGetAllPDFDocs
            : GLOBALS.DEV.baseURLGetAllPDFDocs;

        console.log('Fetching products from:', baseURLGetAllPDFDocs);

        const response = await axios.get(baseURLGetAllPDFDocs);

        if (response.data && Array.isArray(response.data)) {
          console.log('Products fetched successfully:', response.data.length);
          console.log('Sample data:', response.data.slice(0, 2));

          // DEBUG: Check for thumbnail data in products
          response.data.forEach((record, recordIndex) => {
            if (record.products) {
              record.products.forEach((product, productIndex) => {
                console.log(`🔍 Record ${recordIndex}, Product ${productIndex}:`, {
                  name: product.name,
                  pdf_thumbnail: product.pdf_thumbnail,
                  thumbnails: product.thumbnails,
                  barcode_thumbnails: product.barcode_thumbnails,
                  allKeys: Object.keys(product)
                });
              });
            }
          });

          // Generate signed URLs for thumbnails
          console.log('🔗 Generating signed URLs for thumbnails...');
          const recordsWithSignedUrls = await generateSignedUrls(response.data);

          setRowData(recordsWithSignedUrls);
        } else {
          console.error('Invalid response format:', response.data);
          setError('Invalid data format received from server');
        }
      } catch (err) {
        console.error('Error fetching products:', err);

        let errorMessage = 'Failed to load products. Please try again.';

        if (err.code === 'ERR_NETWORK' || err.message.includes('ERR_CONNECTION_REFUSED')) {
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (err.code === 'ECONNREFUSED' || err.message.includes('ECONNREFUSED')) {
          errorMessage = 'The service is currently unavailable. Please try again in a few moments.';
        } else if (err.response?.status === 404) {
          errorMessage = 'Products service not found. Please contact support.';
        } else if (err.response?.status >= 500) {
          errorMessage = 'Server error occurred. Please try again later.';
        } else if (err.response?.status === 401 || err.response?.status === 403) {
          errorMessage = 'Access denied. Please check your permissions.';
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Fetch published job IDs on mount
  useEffect(() => {
    const fetchPublishedJobIds = async () => {
      try {
        const url = process.env.REACT_APP_ENV === "PROD"
          ? GLOBALS.PROD.baseURLGetPublishedJobIds
          : GLOBALS.DEV.baseURLGetPublishedJobIds;
        const response = await axios.get(url);
        if (response.data?.success) {
          setPublishedJobIds(response.data.published_jobs || {});
          setFailedJobIds(response.data.failed_jobs || {});
        }
      } catch (err) {
        console.error('Error fetching published job IDs:', err);
      }
    };
    fetchPublishedJobIds();
  }, []);

  // Fetch publish history on mount
  useEffect(() => {
    const fetchPublishHistory = async () => {
      try {
        const url = process.env.REACT_APP_ENV === "PROD"
          ? GLOBALS.PROD.baseURLGetPublishedReports
          : GLOBALS.DEV.baseURLGetPublishedReports;
        const response = await axios.get(url);
        if (response.data?.status === 'success') {
          setPublishHistory(response.data.data || []);
        }
      } catch (err) {
        console.error('Error fetching publish history:', err);
      }
    };
    fetchPublishHistory();
  }, []);

  // Listen for validation updates to refresh data
  useEffect(() => {
    const handleRefresh = () => {
      console.log('🔄 Received refresh event, reloading products data...');
      // Re-fetch the data to get updated validation status
      const fetchProducts = async () => {
        try {
          setLoading(true);
          const baseURLGetAllPDFDocs =
            process.env.REACT_APP_ENV === "PROD"
              ? GLOBALS.PROD.baseURLGetAllPDFDocs
              : GLOBALS.DEV.baseURLGetAllPDFDocs;

          const response = await axios.get(baseURLGetAllPDFDocs);
          if (response.data && Array.isArray(response.data)) {
            console.log('🔄 Data refreshed successfully');
            setRowData(response.data);
          }
        } catch (err) {
          console.error('Error refreshing data:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchProducts();
    };

    window.addEventListener('refreshProductsData', handleRefresh);

    return () => {
      window.removeEventListener('refreshProductsData', handleRefresh);
    };
  }, []);

  // Handle row selection for Ant Design Table
  const handleRowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);
    },
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) {
      toast.warning('Please select products to delete', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setShowBulkDeleteModal(true);
  };



  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    setBulkDeleteLoading(true);
    try {
      const baseURLDeleteProductsBulk =
        process.env.REACT_APP_ENV === "PROD"
          ? GLOBALS.PROD.baseURLDeleteProductsBulk
          : GLOBALS.DEV.baseURLDeleteProductsBulk;

      const documentIds = selectedRows.map(row =>
        typeof row._id === 'object' ? row._id.$oid : row._id
      );

      const response = await axios.delete(baseURLDeleteProductsBulk, {
        data: { document_ids: documentIds }
      });

      setShowBulkDeleteModal(false);

      if (response.data.status === 'success' || response.data.status === 'partial_success') {
        // Remove successfully deleted documents from the grid
        const updatedRowData = rowData.filter(row => {
          const rowId = typeof row._id === 'object' ? row._id.$oid : row._id;
          return !documentIds.includes(rowId);
        });
        setRowData(updatedRowData);
        setSelectedRows([]);
        setSelectedRowKeys([]);

        // Show success message
        if (response.data.status === 'success') {
          toast.success(`Successfully deleted ${response.data.total_successful} products`, {
            position: "top-right",
            autoClose: 3000,
          });
        } else {
          toast.warning(`Deleted ${response.data.total_successful} products. ${response.data.total_failed} products failed to delete.`, {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } else {
        throw new Error(response.data?.message || "Failed to delete products");
      }

    } catch (error) {
      console.error('Error during bulk delete:', error);
      toast.error('Failed to delete products', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Cancel bulk delete
  const cancelBulkDelete = () => {
    setShowBulkDeleteModal(false);
  };

  // Handle publish
  const handlePublish = async () => {
    if (selectedRows.length === 0) {
      toast.warning('Please select at least one document to publish', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Reset progress state first
    setPublishProgress(0);
    setPublishStatus('');
    console.log('🔄 Reset progress to 0% before starting');

    // Generate unique session ID for this publish operation
    const newSessionId = `publish_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);

    // Show modal and start publishing
    const startTime = Date.now();
    setPublishStartTime(startTime);
    setPublishModalVisible(true);
    setIsPublishing(true);
    setPublishProgress(0);
    setPublishStatus('Initializing publish process...');

    // Small delay to ensure modal is rendered before starting socket connection
    await new Promise(resolve => setTimeout(resolve, 100));

    // SocketIO temporarily disabled to prevent connection errors
    // const socketUrl = process.env.REACT_APP_ENV === "PROD"
    //   ? 'https://your-prod-domain.com'
    //   : 'http://127.0.0.1:5050';

    // const socket = io(socketUrl, {
    //   transports: ['websocket', 'polling']
    // });
    // setWebsocket(socket);

    // Simulate progress updates since SocketIO is disabled
    const simulateProgress = () => {
      const steps = [
        { progress: 10, status: 'Preparing documents...' },
        { progress: 30, status: 'Creating Excel files...' },
        { progress: 60, status: 'Starting nutrād AI processing...' },
        { progress: 90, status: 'Finalizing and uploading files...' }
      ];

      steps.forEach((step, index) => {
        setTimeout(() => {
          setPublishProgress(step.progress);
          setPublishStatus(step.status);
        }, index * 1000); // 1 second intervals
      });
    };

    simulateProgress();

    // SocketIO event handlers temporarily disabled
    // socket.on('connect', () => {
    //   console.log('📡 Socket.IO connected for publish progress');
    //   setPublishStatus('Connected to progress updates...');
    //   socket.emit('join_session', { session_id: newSessionId });
    // });

    // socket.on('progress_update', (data) => {
    //   console.log('📡 Progress update received:', data);
    //   if (data.progress !== undefined) {
    //     console.log(`📊 Updating progress from ${publishProgress}% to ${data.progress}%`);
    //     setPublishProgress(data.progress);
    //   }
    //   if (data.status) {
    //     console.log(`📝 Updating status to: ${data.status}`);
    //     setPublishStatus(data.status);
    //   }
    // });

    // socket.on('connect_error', (error) => {
    //   console.error('📡 Socket.IO connection error:', error);
    //   setPublishStatus('Progress updates unavailable, continuing...');
    // });

    // socket.on('disconnect', () => {
    //   console.log('📡 Socket.IO connection closed');
    // });

    try {
      const baseURLPublishPDF =
        process.env.REACT_APP_ENV === "PROD"
          ? GLOBALS.PROD.baseURLPublishPDF
          : GLOBALS.DEV.baseURLPublishPDF;

      // Get the full data for selected rows using job_id for combined documents
      const selectedData = selectedRows.map((row) => ({
        job_id: row.metadata.job_id,
        case: row.case,
        products: row.products,
        metadata: row.metadata,
      }));

      // Add session ID to the request for WebSocket tracking
      const requestData = {
        documents: selectedData,
        session_id: newSessionId
      };

      const jobIds = selectedRows.map((row) => row.metadata.job_id);
      console.log("📤 Publishing documents with job IDs:", jobIds);
      console.log("📤 Publishing with session ID:", newSessionId);
      console.log("📤 Publishing to URL:", baseURLPublishPDF);
      console.log("🔥 FRONTEND DEBUG: About to send publish request");
      console.log("🔥 FRONTEND DEBUG: Request data:", requestData);

      setPublishStatus('Sending data to server...');

      // Create a separate axios instance with extended timeout
      // Use a function to prevent build optimization
      const getTimeoutMs = () => 600 * 1000; // 10 minutes
      const axiosInstance = axios.create({
        timeout: getTimeoutMs(),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log("🔧 AXIOS INSTANCE TIMEOUT:", axiosInstance.defaults.timeout);

      // Use async publish endpoint to avoid connection timeouts
      const asyncURL = baseURLPublishPDF.replace('/api/publish_pdf', '/api/publish_pdf_async');
      console.log("🔄 Using async publish URL:", asyncURL);

      const startResponse = await axiosInstance.post(asyncURL, requestData);

      if (!startResponse.data.success) {
        throw new Error(startResponse.data.message || 'Failed to start publish job');
      }

      const jobId = startResponse.data.job_id;
      console.log("📋 Publish job started with ID:", jobId);

      setPublishStatus('Job started, processing in background...');
      setPublishProgress(10);

      // Poll for job status
      const statusURL = baseURLPublishPDF.replace('/api/publish_pdf', `/api/publish_status/${jobId}`);
      let completed = false;
      let attempts = 0;
      const maxAttempts = 120; // 10 minutes with 5-second intervals
      let response = null; // Declare response variable

      while (!completed && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
        attempts++;

        try {
          const statusResponse = await axiosInstance.get(statusURL);
          const status = statusResponse.data;

          console.log(`📊 Job ${jobId} status:`, status);

          setPublishProgress(status.progress || 0);
          setPublishStatus(status.message || 'Processing...');

          if (status.status === 'completed') {
            completed = true;
            setPublishProgress(100);
            setPublishStatus('Publishing completed successfully!');

            // Use the result from the async job
            response = {
              status: 200,
              data: status.result || {
                status: 'success',
                success: true,
                message: 'Publish completed successfully'
              }
            };

            // Continue with existing success handling logic...
            console.log('🔍 ASYNC PUBLISH RESPONSE DEBUG [TIMESTAMP: ' + Date.now() + ']:', {
              status: response.status,
              dataStatus: response.data?.status,
              dataSuccess: response.data?.success,
              dataSuccessType: typeof response.data?.success,
              fullData: response.data
            });

            // Process the response (keeping existing logic)
            break;

          } else if (status.status === 'failed') {
            throw new Error(status.message || status.error || 'Publish job failed');
          }

        } catch (statusError) {
          console.error('❌ Error checking job status:', statusError);
          if (attempts >= maxAttempts) {
            throw new Error('Timeout waiting for publish job to complete');
          }
        }
      }

      if (!completed) {
        throw new Error('Publish job timed out after 10 minutes');
      }

      // Debug the response
      console.log('🔍 PUBLISH RESPONSE DEBUG [TIMESTAMP: ' + Date.now() + ']:', {
        status: response.status,
        dataStatus: response.data?.status,
        dataSuccess: response.data?.success,
        dataSuccessType: typeof response.data?.success,
        fullData: response.data
      });

      console.log('🔍 CONDITION CHECK:', {
        statusIs200: response.status === 200,
        hasStatusSuccess: response.data.status === 'success',
        hasSuccessTrue: response.data.success === true,
        hasSuccessString: response.data.success === 'true',
        messageIncludesSuccessfully: response.data.message && response.data.message.includes('successfully')
      });

      // Check for success in multiple possible formats
      const isSuccess = response.status === 200 && (
        response.data.status === 'success' ||
        response.data.success === true ||
        response.data.success === 'true' ||
        (response.data.message && response.data.message.includes('successfully'))
      );

      if (isSuccess) {
        console.log('✅ SUCCESS PATH TAKEN - isSuccess = true');
        // Store the results for the results modal
        setPublishResults({
          ...response.data,
          documentsCount: selectedRows.length,
          timestamp: new Date().toLocaleString()
        });

        // Close progress modal after a brief delay to show completion
        setTimeout(() => {
          setPublishModalVisible(false);
          // Show results modal
          setResultsModalVisible(true);
          // Show success toast
          toast.success('Publishing completed successfully! View details in the results dialog.', {
            position: "top-right",
            autoClose: 5000,
          });
        }, 1500);

        // Clear selection
        setSelectedRows([]);
        setSelectedRowKeys([]);
      } else {
        console.log('❌ ELSE PATH TAKEN - isSuccess = false');
        // Special handling for the AI processing skipped case
        if (response.data?.message && response.data.message.includes("PDF published successfully")) {
          console.log('🎯 DETECTED SUCCESS MESSAGE IN ELSE BLOCK:', response.data.message);
          // Treat this as success
          setPublishResults({
            ...response.data,
            documentsCount: selectedRows.length,
            timestamp: new Date().toLocaleString()
          });

          // Close progress modal after a brief delay to show completion
          setTimeout(() => {
            setPublishModalVisible(false);
            // Show results modal
            setResultsModalVisible(true);
            // Show success toast
            toast.success('Publishing completed successfully! View details in the results dialog.', {
              position: "top-right",
              autoClose: 5000,
            });
          }, 1500);

          // Clear selection
          setSelectedRows([]);
          setSelectedRowKeys([]);
        } else {
          throw new Error(response.data?.message || "Failed to publish documents");
        }
      }
    } catch (err) {
      console.error("Error publishing documents:", err);

      // Close modal and show error
      setPublishModalVisible(false);

      // Close Socket.IO connection (temporarily disabled)
      // if (websocket) {
      //   websocket.disconnect();
      //   setWebsocket(null);
      // }

      let errorMessage = 'Failed to publish documents. Please try again.';

      if (err.code === 'ERR_NETWORK' || err.message.includes('ERR_CONNECTION_REFUSED')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error occurred during publishing. Please try again later.';
      } else if (err.response?.data?.error) {
        errorMessage = `Publish failed: ${err.response.data.error}`;
      } else if (err.response?.data?.message) {
        errorMessage = `Publish failed: ${err.response.data.message}`;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsPublishing(false);
      // Reset progress state
      setPublishProgress(0);
      setPublishStatus('');
      setPublishStartTime(null);
      setElapsedTime(0);

      // Clean up Socket.IO connection (temporarily disabled)
      // if (websocket) {
      //   websocket.disconnect();
      //   setWebsocket(null);
      // }
      setSessionId(null);
    }
  };

  // Handle delete click
  const handleDeleteClick = (record) => {
    setDeleteRowData(record);
    setShowDeleteModal(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    try {
      const baseURLDeleteProduct =
        process.env.REACT_APP_ENV === "PROD"
          ? GLOBALS.PROD.baseURLDeleteProduct
          : GLOBALS.DEV.baseURLDeleteProduct;

      await axios.delete(
        `${baseURLDeleteProduct}/${deleteRowData.metadata.job_id}`
      );

      // Remove from local data
      setRowData(prevData =>
        prevData.filter(row => row.metadata.job_id !== deleteRowData.metadata.job_id)
      );

      setShowDeleteModal(false);
      setDeleteRowData(null);

      toast.success("Document deleted successfully", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Error deleting document:", err);
      toast.error("Failed to delete document", {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  };

  // Handle cell value changes
  const onCellValueChanged = async (params) => {
    try {
      console.log('💾💾💾 === onCellValueChanged CALLED ===');
      console.log('📦 Received params:', params);
      console.log('📦 Has validationResult?', !!params.validationResult);
      if (params.validationResult) {
        console.log('📦 ValidationResult:', params.validationResult);
      }

      const baseURLSavePDFToMongo =
        process.env.REACT_APP_ENV === "PROD"
          ? GLOBALS.PROD.baseURLSavePDFToMongo
          : GLOBALS.DEV.baseURLSavePDFToMongo;

      const jobId = params.data.metadata?.job_id;
      if (!jobId) {
        throw new Error("Job ID not found");
      }

      // Update the local data structure first
      const fieldPath = params.column.colDef.field;

      // Create a deep copy of the current record
      const updatedRecord = JSON.parse(JSON.stringify(params.data));

      // Handle nested path updates
      if (fieldPath.includes('.')) {
        const parts = fieldPath.split('.');
        let current = updatedRecord;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {};
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = params.newValue;
      } else {
        updatedRecord[fieldPath] = params.newValue;
      }

      // Handle GTIN validation results if present
      if (params.validationResult) {
        const { field, gtin_valid } = params.validationResult;

        // Determine where to store validation based on field type
        if (field === 'Case UPC') {
          // Store case validation in case.validation
          if (!updatedRecord.case) updatedRecord.case = {};
          if (!updatedRecord.case.validation) updatedRecord.case.validation = {};
          updatedRecord.case.validation.gtin_valid = gtin_valid;

          console.log('✅ Added case validation to case.validation.gtin_valid:', gtin_valid);
        } else if (field === 'Retail Pkg. UPC' || field === 'Retail Pkg UPC') {
          // Store product validation in the specific product
          const pathParts = fieldPath.split('.');
          if (pathParts[0] === 'products' && pathParts[1]) {
            const productIndex = parseInt(pathParts[1]);
            if (updatedRecord.products && updatedRecord.products[productIndex]) {
              if (!updatedRecord.products[productIndex].validation) {
                updatedRecord.products[productIndex].validation = {};
              }
              updatedRecord.products[productIndex].validation.gtin_valid = gtin_valid;

              console.log(`✅ Added product ${productIndex} validation to products[${productIndex}].validation.gtin_valid:`, gtin_valid);
            }
          }
        }

        console.log('Added validation result to document:', {
          field,
          gtin_valid,
          fieldPath,
          validation_stored: true
        });
      }

      // Prepare the updated document for saving
      const updatedDocument = {
        ...updatedRecord,
        upsert: true,
        save_as_single_document: true,
        prevent_product_splitting: true,
        skip_duplicate_check: true  // Skip duplicate detection for field updates
      };

      console.log('💾 Updating document with field:', {
        job_id: jobId,
        field: fieldPath,
        new_value: params.newValue,
        old_value: params.oldValue
      });

      console.log('💾 Document validation status:', {
        case_validation: updatedDocument.case?.validation?.gtin_valid,
        products_validation: updatedDocument.products?.map(p => p.validation?.gtin_valid)
      });

      console.log('💾 Sending to backend:', {
        url: baseURLSavePDFToMongo,
        hasValidation: !!updatedDocument.validation,
        documentKeys: Object.keys(updatedDocument)
      });

      // Make POST request to save the updated document
      const response = await axios.post(
        baseURLSavePDFToMongo,
        updatedDocument,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        console.log('Document updated successfully:', response.data);

        // Update the local rowData state to reflect the change
        setRowData(prevRowData =>
          prevRowData.map(row =>
            row.metadata?.job_id === jobId ? updatedRecord : row
          )
        );

        toast.success("Field updated successfully", {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        throw new Error("Failed to update field");
      }

    } catch (err) {
      console.error("Error updating field:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      console.error("Error message:", err.message);

      // Note: Duplicate detection is disabled for field updates
      // It should only occur during PDF imports

      // Handle different types of errors
      let errorMessage = 'Failed to update field. Please try again.';

      if (err.code === 'ERR_NETWORK' || err.message.includes('ERR_CONNECTION_REFUSED')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (err.code === 'ECONNREFUSED' || err.message.includes('ECONNREFUSED')) {
        errorMessage = 'The update service is currently unavailable. Please try again in a few moments.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Document not found. Please refresh the page and try again.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error occurred during update. Please try again later.';
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = 'Access denied for updating. Please check your permissions.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid update data. Please check your input and try again.';
      } else if (err.response?.data?.message) {
        errorMessage = `Update failed: ${err.response.data.message}`;
      }

      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  };

  // Date range filter
  const [dayFilter, setDayFilter] = useState(null); // null = All

  const filteredData = useMemo(() => {
    if (!dayFilter) return rowData;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - dayFilter);
    return rowData.filter(r => {
      const ts = r.metadata?.local_timestamp || r.metadata?.timestamp;
      return ts && new Date(ts) >= cutoff;
    });
  }, [rowData, dayFilter]);

  // Derive filter options dynamically from loaded data
  const customerFilters = useMemo(() =>
    [...new Set(rowData.map(r => r.metadata?.customer).filter(Boolean))].sort()
      .map(c => ({ text: c, value: c })),
    [rowData]
  );

  const productTypeFilters = useMemo(() =>
    [...new Set(rowData.map(r => r.case?.case_information?.['Product Type']).filter(Boolean))].sort()
      .map(t => ({ text: t, value: t })),
    [rowData]
  );

  const gtinValidationFilters = [
    { text: 'All Valid', value: 'all' },
    { text: 'Some Valid', value: 'some' },
    { text: 'None Valid', value: 'none_valid' },
    { text: 'No Data', value: 'none' },
  ];

  // Define columns for the master table
  const columns = [
    {
      title: 'Name',
      key: 'name',
      width: 250,
      fixed: 'left',
      sorter: (a, b) => (a.case?.case_information?.['Haribo Item No'] || '').localeCompare(b.case?.case_information?.['Haribo Item No'] || ''),
      render: (_, record) => {
        const nameValue = record.case?.case_information?.['Haribo Item No'] || 'N/A';

        // Get the first product's first image for thumbnail
        const firstProduct = record.products?.[0];
        const thumbnailUrl = firstProduct?.pdf_thumbnail_url || firstProduct?.thumbnail_urls?.[0] || firstProduct?.barcode_thumbnail_urls?.[0];
        const originalImageUrl = firstProduct?.pdf_thumbnail_url || firstProduct?.images?.[0] || firstProduct?.barcode_images?.[0];

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ProductThumbnail
              thumbnailUrl={thumbnailUrl}
              originalImageUrl={originalImageUrl}
              productName={nameValue}
              size={150}
              showTooltip={false}
              onClick={null}
              style={{ borderRadius: 0 }}
            />
            <Text strong>{nameValue}</Text>
          </div>
        );
      },
    },
    {
      title: 'Product Type',
      dataIndex: ['case', 'case_information', 'Product Type'],
      key: 'productType',
      width: 200,
      sorter: (a, b) => (a.case?.case_information?.['Product Type'] || '').localeCompare(b.case?.case_information?.['Product Type'] || ''),
      filters: productTypeFilters,
      filterMultiple: true,
      onFilter: (value, record) => (record.case?.case_information?.['Product Type'] || '') === value,
      render: (text) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 150,
      sorter: (a, b) => (a.metadata?.customer || '').localeCompare(b.metadata?.customer || ''),
      filters: customerFilters,
      filterMultiple: true,
      onFilter: (value, record) => (record.metadata?.customer || '') === value,
      render: (_, record) => <span>{record.metadata?.customer || '—'}</span>,
    },
    {
      title: 'Products Count',
      key: 'productsCount',
      width: 120,
      sorter: (a, b) => (a.products?.length || 0) - (b.products?.length || 0),
      render: (_, record) => (
        <Tag color="blue">{record.products?.length || 0}</Tag>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: ['metadata', 'timestamp'],
      key: 'createdDate',
      width: 200,
      defaultSortOrder: 'descend',
      sorter: (a, b) => {
        const tsA = a.metadata?.local_timestamp || a.metadata?.timestamp || '';
        const tsB = b.metadata?.local_timestamp || b.metadata?.timestamp || '';
        return new Date(tsA).getTime() - new Date(tsB).getTime();
      },
      render: (text, record) => {
        // Try to use local_timestamp if available, otherwise use timestamp
        const timestampToUse = record.metadata?.local_timestamp || text;

        if (!timestampToUse) return '-';

        try {
          // Create date object from UTC timestamp
          let date;

          // Handle different timestamp formats
          if (typeof timestampToUse === 'string') {
            // If it's an ISO string or UTC string, parse it directly
            date = new Date(timestampToUse);
          } else if (typeof timestampToUse === 'number') {
            // If it's a Unix timestamp, convert to milliseconds if needed
            date = new Date(timestampToUse < 10000000000 ? timestampToUse * 1000 : timestampToUse);
          } else {
            date = new Date(timestampToUse);
          }

          // Check if the date is valid
          if (isNaN(date.getTime())) {
            console.warn('Invalid date:', timestampToUse);
            return 'Invalid Date';
          }

          // Format as local date and time using the user's locale
          return date.toLocaleString(navigator.language || 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZoneName: 'short'
          });

        } catch (error) {
          console.error('Error formatting date:', error, 'Value:', timestampToUse);
          return 'Invalid Date';
        }
      },
    },
    {
      title: 'Published',
      key: 'published',
      width: 160,
      align: 'center',
      filters: [
        { text: 'Published', value: 'published' },
        { text: 'Publish Failed', value: 'failed' },
        { text: 'Not Published', value: 'not_published' },
      ],
      onFilter: (value, record) => {
        const jobId = record.metadata?.job_id;
        const isPublished = !!publishedJobIds[jobId];
        const isFailed = !isPublished && !!failedJobIds[jobId];
        if (value === 'published') return isPublished;
        if (value === 'failed') return isFailed;
        return !isPublished && !isFailed;
      },
      render: (_, record) => {
        const jobId = record.metadata?.job_id;
        const publishInfo = publishedJobIds[jobId];
        if (publishInfo) {
          const date = publishInfo.published_at
            ? new Date(publishInfo.published_at).toLocaleDateString()
            : '';
          return <Tag color="green" style={{ fontSize: '11px' }}>✓ Published {date}</Tag>;
        }
        const failInfo = failedJobIds[jobId];
        if (failInfo) {
          const date = failInfo.failed_at
            ? new Date(failInfo.failed_at).toLocaleDateString()
            : '';
          return (
            <Tag color="red" style={{ fontSize: '11px' }} title={failInfo.error_message || ''}>
              ✗ Publish Failed {date}
            </Tag>
          );
        }
        return <Tag color="default" style={{ fontSize: '11px' }}>Not Published</Tag>;
      },
    },
    {
      title: 'GTIN Validation',
      key: 'gtinValidation',
      width: 150,
      align: 'center',
      filters: gtinValidationFilters,
      filterMultiple: true,
      onFilter: (value, record) => getGTINValidationStatus(record) === value,
      render: (_, record) => {
        return renderGTINValidationIcon(record);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Product Management ({dayFilter ? `${filteredData.length} of ${rowData.length}` : rowData.length})</Title>
      <Text>Manage your case and product information with editable fields.</Text>

      <div style={{ marginTop: '20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading products...</div>
          </div>
        ) : error ? (
          <Alert
            message="Error Loading Products"
            description={error}
            type="error"
            showIcon
          />
        ) : rowData.length === 0 ? (
          <Alert
            message="No Documents Available"
            description="There are currently no documents to display. Documents will appear here once they are uploaded and processed."
            type="info"
            showIcon
            style={{ textAlign: 'center' }}
          />
        ) : (
          <div>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Space>
                <Button
                  type="primary"
                  ghost
                  disabled={selectedRows.length === 0}
                  loading={isPublishing}
                  onClick={handlePublish}
                  icon={isPublishing ? null : <CloudUploadOutlined />}
                >
                  {isPublishing
                    ? `Publishing ${selectedRows.length} document(s)...`
                    : `Publish Selected (${selectedRows.length})`
                  }
                </Button>
                <Button
                  danger
                  ghost
                  disabled={selectedRows.length === 0}
                  onClick={handleBulkDelete}
                  icon={<DeleteOutlined />}
                >
                  Delete Selected ({selectedRows.length})
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                  icon={<ReloadOutlined />}
                >
                  Refresh
                </Button>
              </Space>
              <Space>
                {[null, 30, 60, 90].map(days => (
                  <Button
                    key={days ?? 'all'}
                    type={dayFilter === days ? 'primary' : 'default'}
                    ghost={dayFilter === days}
                    size="small"
                    onClick={() => setDayFilter(days)}
                  >
                    {days ? `Last ${days} Days` : 'All'}
                  </Button>
                ))}
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={filteredData}
              rowKey={(record) => {
                // Create a unique key for each record
                // Priority: _id (MongoDB ObjectId) > job_id + product_id > job_id + index
                if (record._id) {
                  return typeof record._id === 'object' ? record._id.$oid : record._id;
                }

                // If we have a product_id in the first product, use job_id + product_id
                const firstProductId = record.products?.[0]?.product_id;
                if (firstProductId) {
                  return `${record.metadata?.job_id}-${firstProductId}`;
                }

                // Fallback: use job_id + timestamp for uniqueness
                const timestamp = record.metadata?.timestamp || Date.now();
                return `${record.metadata?.job_id}-${timestamp}`;
              }}
              size="small"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              rowSelection={handleRowSelection}
              expandable={{
                expandedRowRender: (record) => (
                  <DetailComponent
                    record={record}
                    onUpdate={onCellValueChanged}
                  />
                ),
                rowExpandable: (record) => record.products?.length > 0,
              }}
              scroll={{ x: 1000 }}
              loading={loading}
            />
          </div>
        )}
      </div>

      {/* Publish History Section */}
      {publishHistory.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <Collapse
            items={[{
              key: 'publish-history',
              label: (
                <span>
                  <Badge count={publishHistory.length} style={{ backgroundColor: '#52c41a', marginRight: '8px' }} />
                  Publish History
                </span>
              ),
              children: (
                <Table
                  size="small"
                  dataSource={publishHistory}
                  rowKey={(r) => r._id || r.created_at}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  columns={[
                    {
                      title: 'Published At',
                      dataIndex: 'created_at',
                      key: 'created_at',
                      width: 200,
                      render: (val) => val ? new Date(val).toLocaleString() : '—',
                      defaultSortOrder: 'descend',
                      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
                    },
                    {
                      title: 'Published By',
                      dataIndex: 'user_email',
                      key: 'user_email',
                      width: 220,
                      render: (val) => val || '—',
                    },
                    {
                      title: 'Documents Published',
                      dataIndex: 'processed_job_ids',
                      key: 'processed_job_ids',
                      width: 180,
                      align: 'center',
                      render: (ids) => <Badge count={ids?.length || 0} style={{ backgroundColor: '#1677ff' }} showZero />,
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      width: 120,
                      align: 'center',
                      render: (status) => (
                        <Tag color={status === 'success' ? 'green' : 'red'}>
                          {status === 'success' ? '✓ Success' : status}
                        </Tag>
                      ),
                    },
                  ]}
                />
              ),
            }]}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {/* Publish Progress Modal */}
      <Modal
        title="Publishing Documents"
        open={publishModalVisible}
        footer={null}
        closable={publishProgress === 100}
        centered
        width={500}
      >
        <div style={{ position: 'relative', textAlign: 'center', padding: '20px 0' }}>
          <Spin size="large" style={{ marginBottom: '20px' }} />
          <div style={{ marginBottom: '20px' }}>
            <Progress
              percent={publishProgress}
              status={publishProgress === 100 ? 'success' : 'active'}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
          </div>
          <div style={{ fontSize: '16px', color: '#666', marginBottom: '10px' }}>
            {publishStatus}
          </div>
          <div style={{ fontSize: '14px', color: '#999' }}>
            Processing {selectedRows.length} document(s)...
          </div>
          <div style={{ fontSize: '12px', color: '#ccc', marginTop: '10px' }}>
            This may take a few minutes depending on the data size and nutrād AI processing.
          </div>

          {/* Timer in lower left corner */}
          <div style={{
            position: 'absolute',
            bottom: '10px',
            left: '10px',
            fontSize: '12px',
            color: '#999',
            fontFamily: 'monospace'
          }}>
            ⏱️ {formatElapsedTime(elapsedTime)}
          </div>
        </div>
      </Modal>

      {/* Publish Results Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CheckCircleFilled style={{ color: '#52c41a', fontSize: '20px' }} />
            <span>Publishing Complete</span>
          </div>
        }
        open={resultsModalVisible}
        onOk={() => setResultsModalVisible(false)}
        onCancel={() => setResultsModalVisible(false)}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        centered
        width={700}
        zIndex={2000}
        mask={true}
        maskClosable={true}
      >
        {publishResults && (
          <div style={{ padding: '20px 0' }}>
            {/* Summary Section */}
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#389e0d', marginBottom: '8px' }}>
                ✅ Successfully Published {publishResults.documentsCount} Document(s)
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Completed at: {publishResults.timestamp}
              </div>
            </div>

            {/* Processing Statistics */}
            {publishResults.mapping_statistics && (
              <div style={{ marginBottom: '24px' }}>
                <Title level={5} style={{ marginBottom: '12px' }}>📊 Processing Statistics</Title>
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                        {publishResults.mapping_statistics.total_mapped_fields}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Total Fields Mapped</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                        {publishResults.mapping_statistics.mapping_percentage}%
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Mapping Coverage</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ padding: '12px', backgroundColor: '#fff7e6', borderRadius: '6px' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                        {publishResults.mapping_statistics.processing_time_seconds}s
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Processing Time</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ padding: '12px', backgroundColor: '#f9f0ff', borderRadius: '6px' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                        {publishResults.processed_products?.length || 0}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Products Processed</div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            {/* Field Mapping Details */}
            {publishResults.mapping_statistics && (
              <div style={{ marginBottom: '24px' }}>
                <Title level={5} style={{ marginBottom: '12px' }}>🔄 Field Mapping Details</Title>
                <div style={{ padding: '16px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
                  <Row gutter={[16, 8]}>
                    <Col span={6}>
                      <Text strong>Source Fields:</Text>
                      <div style={{ fontSize: '18px', color: '#1890ff' }}>
                        {publishResults.mapping_statistics.mapped_fields}
                      </div>
                    </Col>
                    <Col span={6}>
                      <Text strong>Static Fields:</Text>
                      <div style={{ fontSize: '18px', color: '#52c41a' }}>
                        {publishResults.mapping_statistics.static_fields_added || 0}
                      </div>
                    </Col>
                    <Col span={6}>
                      <Text strong>Computed Fields:</Text>
                      <div style={{ fontSize: '18px', color: '#fa8c16' }}>
                        {publishResults.mapping_statistics.computed_fields_added || 0}
                      </div>
                    </Col>
                    <Col span={6}>
                      <Text strong>Unmapped Fields:</Text>
                      <div style={{ fontSize: '18px', color: '#ff4d4f' }}>
                        {publishResults.mapping_statistics.unmapped_fields}
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            )}

            {/* Files Generated */}
            <div>
              <Title level={5} style={{ marginBottom: '12px' }}>📁 Files Generated</Title>
              <div style={{ padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '6px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Source Excel File:</Text>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px', padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                    <FileExcelOutlined style={{ color: '#52c41a', fontSize: '24px', marginRight: '12px' }} />
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      {publishResults.source_file?.split('/').pop() || 'Generated successfully'}
                    </span>
                  </div>
                </div>
                <div>
                  <Text strong>Mapped Output File:</Text>
                  <div style={{ marginTop: '8px', padding: '16px', backgroundColor: '#f6ffed', border: '2px solid #52c41a', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FileExcelOutlined style={{ color: '#52c41a', fontSize: '32px', marginRight: '12px' }} />
                        <div>
                          <div style={{ fontSize: '16px', color: '#333', fontWeight: '600' }}>
                            {publishResults.s3_info?.filename || publishResults.mapped_file?.split('/').pop() || 'mapped_output.xlsx'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '2px' }}>
                            ✅ Ready for download
                          </div>
                        </div>
                      </div>
                      <Button
                        type="primary"
                        size="large"
                        icon={<DownloadOutlined />}
                        onClick={() => {
                          if (publishResults.s3_info?.download_url) {
                            // Use the presigned URL from backend
                            window.open(publishResults.s3_info.download_url, '_blank');
                          } else {
                            // Fallback to toast message
                            toast.error('Download URL not available. Please try publishing again.', {
                              position: "top-right",
                              autoClose: 5000,
                            });
                          }
                        }}
                        disabled={!publishResults.s3_info?.download_url}
                        style={{
                          backgroundColor: '#52c41a',
                          borderColor: '#52c41a',
                          fontSize: '14px',
                          height: '40px',
                          minWidth: '120px'
                        }}
                      >
                        Download XLSX
                      </Button>
                    </div>
                    {publishResults.s3_info && (
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        <div>📁 S3 Bucket: {publishResults.s3_info.bucket}</div>
                        <div style={{ marginTop: '2px' }}>🔗 S3 Key: {publishResults.s3_info.key}</div>
                        <div style={{ marginTop: '2px', color: '#52c41a' }}>
                          ✅ File uploaded to cloud storage and ready for download
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Confirm Delete"
        open={showDeleteModal}
        onOk={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteRowData(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        icon={<ExclamationCircleOutlined />}
      >
        <p>Are you sure you want to delete this document?</p>
        <p><strong>Job ID:</strong> {deleteRowData?.metadata?.job_id}</p>
        <p>This action cannot be undone.</p>
      </Modal>

      {/* Duplicate Detection Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', color: '#fa8c16' }}>
            <ExclamationCircleOutlined style={{ marginRight: '8px', fontSize: '20px' }} />
            Document Already Exists
          </div>
        }
        open={duplicateModalVisible}
        onCancel={() => {
          setDuplicateModalVisible(false);
          setDuplicateInfo(null);
          setNewDataForReplacement(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setDuplicateModalVisible(false);
              setDuplicateInfo(null);
              setNewDataForReplacement(null);
            }}
            style={{ marginRight: '8px' }}
          >
            Keep Existing
          </Button>,
          <Button
            key="overwrite"
            type="primary"
            onClick={handleReplaceDuplicate}
            ghost
          >
            Overwrite with New Data
          </Button>
        ]}
        width={600}
      >
        {duplicateInfo && (
          <div>
            <div style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.5' }}>
              <Text>A document with <Text strong>Haribo Item No "{duplicateInfo.haribo_item_no}"</Text> already exists in the system.</Text>
            </div>

            <div style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '500', color: '#1890ff' }}>
              Do you want to overwrite the existing document with this new data?
            </div>

            <div style={{ marginBottom: '20px' }}>
              <Text strong>Existing Document Details:</Text>
              <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div><Text strong>Job ID:</Text> {duplicateInfo.existing_job_id}</div>
                <div style={{ marginTop: '4px' }}>
                  <Text strong>Created:</Text> {duplicateInfo.existing_timestamp ? new Date(duplicateInfo.existing_timestamp).toLocaleString() : 'Unknown'}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
              <Text strong style={{ color: '#fa8c16' }}>⚠️ Important:</Text>
              <ul style={{ marginTop: '8px', marginBottom: '0', color: '#666' }}>
                <li>Choosing "Overwrite" will completely replace the existing document</li>
                <li>All previous data will be lost and cannot be recovered</li>
                <li>Choosing "Keep Existing" will cancel this upload</li>
              </ul>
            </div>

            <Text type="secondary" style={{ fontSize: '12px' }}>
              💡 Make sure you have the correct Haribo Item No before proceeding.
            </Text>
          </div>
        )}
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        title="Confirm Bulk Delete"
        open={showBulkDeleteModal}
        onCancel={cancelBulkDelete}
        footer={[
          <Button key="cancel" onClick={cancelBulkDelete} disabled={bulkDeleteLoading}>
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            loading={bulkDeleteLoading}
            onClick={confirmBulkDelete}
          >
            {bulkDeleteLoading ? 'Deleting...' : `Delete ${selectedRows.length} Products`}
          </Button>
        ]}
        width={600}
      >
        <div>
          <div style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.5' }}>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
            <Text>Are you sure you want to delete <Text strong>{selectedRows.length}</Text> selected products?</Text>
          </div>

          <div style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '500', color: '#ff4d4f' }}>
            This action cannot be undone.
          </div>

          <div style={{ marginBottom: '20px' }}>
            <Text strong>Products to be deleted:</Text>
            <div style={{
              marginTop: '8px',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {selectedRows.map((row) => {
                // Create unique key for each row in the delete modal
                const uniqueKey = row._id
                  ? (typeof row._id === 'object' ? row._id.$oid : row._id)
                  : `${row.metadata?.job_id}-${row.products?.[0]?.product_id || Date.now()}`;

                return (
                  <div key={uniqueKey} style={{ marginBottom: '4px' }}>
                    <Text>• {row.case_information?.['Haribo Item No'] || uniqueKey || 'Unknown'}</Text>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff2f0', borderRadius: '6px', border: '1px solid #ffccc7' }}>
            <Text strong style={{ color: '#ff4d4f' }}>⚠️ Warning:</Text>
            <ul style={{ marginTop: '8px', marginBottom: '0', color: '#666' }}>
              <li>All selected products and their associated data will be permanently deleted</li>
              <li>This action cannot be undone</li>
              <li>Make sure you have selected the correct products</li>
            </ul>
          </div>
        </div>
      </Modal>

      <Footer />
      <ToastContainer />
    </div>
  );
}

export default Products;
